import { Box, Grid, IconButton, TextField, Typography } from '@mui/material';
import React, { forwardRef, memo, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import {
    CannedResponseTemplate,
    sendCannedResponsePayload,
    sendTemplatePayload,
    uploadedMedia,
} from '../../../../../models/axiosModel';
import styles from '../../../../../assets/styles/componentStyles/chatbotStyles/chats.module.scss';
import IconsColor from '../../../../../components/utility/iconsColor';
import { handleKeyDownForFormatting } from '../../../../../components/dashboard/templatelibrary/constant';
import { UserContext, UserContextPayload } from '../../../../../context/userContext';
import { useShallow } from 'zustand/react/shallow';
import { getDynamicColorsBySource, isChannelNameChronoxAiWebApp, isChannelNameWhatsApp, sendCannedResMsg } from '../../../utils';
import { useRuleBasedInboxStore } from '../../../../../store/ruleBasedInboxStore';
import { hexToRgba } from '../../../../../utils/constants';

interface formatType {
    bold?: boolean;
    italic?: boolean;
    strikethrough?: boolean;
    bullet?: boolean;
}

interface toolbarItemsType {
    key: string;
    label: string;
    icon: React.ReactNode;
    fontSize?: 'small' | 'medium' | 'large';
}

const TOOLBAR_ITEMS: toolbarItemsType[] = [
    { key: 'bold', label: 'Bold', icon: <FormatBoldIcon fontSize="small" /> },
    { key: 'italic', label: 'Italic', icon: <FormatItalicIcon fontSize="small" /> },
    { key: 'strikethrough', label: 'Strikethrough', icon: <StrikethroughSIcon fontSize="small" /> },
    { key: 'bullet', label: 'Bullet', icon: <FormatListBulletedIcon fontSize="small" /> },
];

interface SlashMenuState {
    active: boolean;
    query: string;
    highlightedIndex: number;
}

const INITIAL_SLASH_MENU: SlashMenuState = {
    active: false,
    query: '',
    highlightedIndex: 0,
};

const scheduleAfterRender = (fn: () => void) => {
    requestAnimationFrame(() => {
        requestAnimationFrame(fn);
    });
};

export type ChatTextFieldHandle = {
    insertAtCursor: (text: string) => void;
};

type DynamicColors = ReturnType<typeof getDynamicColorsBySource>;

export interface ChatTextFieldProps {
    handleSendMessageManually: (
        inputValue: string,
        flagMediaMessage: boolean,
        flagCannedResponseTemplate: boolean,
        flagTemplateMessage: boolean,
        selectedMediaSet: uploadedMedia[],
        sendTemplatePayload: sendTemplatePayload | null,
        cannedResponsePayload?: sendCannedResponsePayload
    ) => Promise<void>;
    dynamicColors: DynamicColors;
    textfieldWrapperStyle: React.CSSProperties;
    textFieldSx: object;
    isManualOverrideTextfieldDisabled: boolean;
    placeholder: string;
    shouldShowSendBtn: boolean;
    /** Stable adornment nodes from parent — must not depend on inputValue */
    startAdornment: React.ReactNode;
    endAdornment: React.ReactNode;
}

const ChatTextField = forwardRef<ChatTextFieldHandle, ChatTextFieldProps>(function ChatTextField(props, ref) {
    const { handleSendMessageManually, dynamicColors, textfieldWrapperStyle, textFieldSx, isManualOverrideTextfieldDisabled,
        placeholder, shouldShowSendBtn, startAdornment, endAdornment
    } = props;

    const { cannedResponseList, setCurrentCannedRes, setFlagPreviewAndSendCannedResPopup, getCannedResponseDataApi, 
        currentActiveChatbotThread, setThreadDraft, clearThreadDraft
    } = useRuleBasedInboxStore(
        useShallow((state) => ({
            cannedResponseList: state.cannedResponseList,
            setCurrentCannedRes: state.setCurrentCannedRes,
            setFlagPreviewAndSendCannedResPopup: state.setFlagPreviewAndSendCannedResPopup,
            getCannedResponseDataApi: state.getCannedResponseDataApi,
            currentActiveChatbotThread: state.currentActiveChatbotThread,
            setThreadDraft: state.setThreadDraft,
            clearThreadDraft: state.clearThreadDraft,
        }))
    );

    const { setFlagLoading, handleError, userDetail } = useContext(UserContext) as UserContextPayload;

    const draftThreadId = currentActiveChatbotThread?.chatbot_user_id?.toString() ?? null;

    const isTextFieldDisabled =
        isManualOverrideTextfieldDisabled || isChannelNameChronoxAiWebApp(currentActiveChatbotThread);

    const sendBtnGridSx = useMemo(() => ({
        backgroundColor: dynamicColors.send_msg_bg_color,
    }), [dynamicColors.send_msg_bg_color]);

    const [inputValue, setinputValue] = useState<string>('');
    const [slashMenu, setSlashMenu] = useState<SlashMenuState>(INITIAL_SLASH_MENU);
    const slashMenuListRef = useRef<HTMLDivElement>(null);
    const inputAreaRef = useRef<HTMLDivElement>(null);
    const slashStartIndexRef = useRef<number>(-1);
    const skipNextCursorSlashDetectRef = useRef(false);
    const lastToolbarSelectionKeyRef = useRef<string | null>(null);
    const inputRef = useRef<HTMLTextAreaElement | null>(null);
    const toolbarRef = useRef<HTMLDivElement | null>(null);
    const draftThreadIdRef = useRef<string | null>(draftThreadId);
    draftThreadIdRef.current = draftThreadId;

    const { active: slashCommandActive, query: slashQuery, highlightedIndex } = slashMenu;

    const [selection, setSelection] = useState<{ start: number; end: number; text: string } | null>(null);
    const [toolbarPosition, setToolbarPosition] = useState<{ top: number; left: number } | null>(null);

    const updateInputValue = useCallback((valueOrUpdater: string | ((prev: string) => string)) => {
        const threadId = draftThreadIdRef.current;
        if (typeof valueOrUpdater === 'string') {
            if (threadId) {
                setThreadDraft(threadId, valueOrUpdater);
            }
            setinputValue((prev) => (draftThreadIdRef.current !== threadId ? prev : valueOrUpdater));
            return;
        }
        setinputValue((prev) => {
            if (draftThreadIdRef.current !== threadId) {
                // Persist to the thread that owned this keystroke; don't clobber the restored draft.
                if (threadId) {
                    const storeDraft = useRuleBasedInboxStore.getState().threadDraftById[threadId] ?? '';
                    setThreadDraft(threadId, valueOrUpdater(storeDraft));
                }
                return prev;
            }
            const next = valueOrUpdater(prev);
            if (threadId) {
                setThreadDraft(threadId, next);
            }
            return next;
        });
    }, [setThreadDraft]);

    const clearInputValue = useCallback(() => {
        const threadId = draftThreadIdRef.current;
        if (threadId) {
            clearThreadDraft(threadId);
        }
        setinputValue((prev) => (draftThreadIdRef.current !== threadId ? prev : ''));
    }, [clearThreadDraft]);

    const filteredCannedResponses = useMemo(() => {
        if (!slashCommandActive || !cannedResponseList) return [];
        const query = slashQuery?.toLowerCase()?.trim() ?? '';
        if (!query) return cannedResponseList;
        return cannedResponseList?.filter((item) => item?.template_title?.toLowerCase()?.includes(query));
    }, [slashCommandActive, slashQuery, cannedResponseList]);

    useEffect(() => {
        if (slashCommandActive && slashMenuListRef?.current) {
            const el = slashMenuListRef?.current?.querySelector(`[data-index="${highlightedIndex}"]`);
            el?.scrollIntoView({ block: 'nearest' });
        }
    }, [highlightedIndex, slashCommandActive]);

    useEffect(() => {
        if (!slashCommandActive) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (inputAreaRef.current && !inputAreaRef.current.contains(e.target as Node)) {
                setSlashMenu(INITIAL_SLASH_MENU);
                slashStartIndexRef.current = -1;
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [slashCommandActive]);

    const deactivateSlashMenu = useCallback(() => {
        setSlashMenu((prev) => (
            prev.active || prev.query || prev.highlightedIndex ? INITIAL_SLASH_MENU : prev
        ));
        slashStartIndexRef.current = -1;
    }, []);

    const detectSlashCommand = useCallback((value: string, cursorPos: number) => {
        if (!value) {
            deactivateSlashMenu();
            return;
        }

        const textBeforeCursor = value.substring(0, cursorPos);
        const lastSlashIndex = textBeforeCursor.lastIndexOf('/');

        if (lastSlashIndex === -1) {
            deactivateSlashMenu();
            return;
        }

        const charBeforeSlash = lastSlashIndex > 0 ? textBeforeCursor[lastSlashIndex - 1] : null;
        const isValidTrigger =
            charBeforeSlash === null ||
            charBeforeSlash === ' ' ||
            charBeforeSlash === '\n' ||
            charBeforeSlash === '\t';

        if (isValidTrigger) {
            const queryAfterSlash = textBeforeCursor.substring(lastSlashIndex + 1);
            if (!queryAfterSlash.includes(' ') && !queryAfterSlash.includes('\n')) {
                slashStartIndexRef.current = lastSlashIndex;
                setSlashMenu((prev) => {
                    if (prev.active && prev.query === queryAfterSlash && prev.highlightedIndex === 0) {
                        return prev;
                    }
                    return { active: true, query: queryAfterSlash, highlightedIndex: 0 };
                });
                return;
            }
        }

        deactivateSlashMenu();
    }, [deactivateSlashMenu]);

    const handleInputChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const value = event.target.value;
            const cursorPos = event.target.selectionStart ?? value.length;
            updateInputValue(value);
            if(!isChannelNameWhatsApp(currentActiveChatbotThread)) return;
            if (currentActiveChatbotThread) {
                detectSlashCommand(value, cursorPos);
                skipNextCursorSlashDetectRef.current = true;
            } else if (!value) {
                deactivateSlashMenu();
            }
        },
        [currentActiveChatbotThread, detectSlashCommand, deactivateSlashMenu, updateInputValue]
    );

    const handleCursorChange = useCallback(
        (event: React.SyntheticEvent) => {
            if(!isChannelNameWhatsApp(currentActiveChatbotThread)) return;
            if (skipNextCursorSlashDetectRef.current) {
                skipNextCursorSlashDetectRef.current = false;
                return;
            }

            const target = event.target as HTMLTextAreaElement;
            const cursorPos = target.selectionStart ?? target.value.length;
            const selectionEnd = target.selectionEnd ?? 0;

            if (cursorPos !== selectionEnd) {
                deactivateSlashMenu();
                return;
            }
            if (currentActiveChatbotThread) {
                detectSlashCommand(target.value, cursorPos);
            }
        },
        [currentActiveChatbotThread, detectSlashCommand, deactivateSlashMenu]
    );

    const getCannedResponseApi = useCallback(
        async (canned_response_template_id: string, flagDirectSend: boolean) => {
            setFlagLoading((prev) => ({ ...prev, flagRoundLoader: true }));
            const res = await getCannedResponseDataApi(canned_response_template_id);
            if (!res.status) {
                handleError(res);
            } else {
                const responseMessages = res.data?.[0]?.canned_response_messages?.sort(
                    (a: any, b: any) => a.message_sequence - b.message_sequence
                );
                const data = {
                    ...res.data?.[0],
                    canned_response_messages: responseMessages?.map((msg: any) => ({
                        ...msg,
                        content_type_id: msg?.content_type?.id,
                    })),
                };
                setCurrentCannedRes(data);
                if (flagDirectSend) {
                    await sendCannedResMsg(data, handleSendMessageManually, userDetail);
                } else {
                    setFlagPreviewAndSendCannedResPopup(true);
                }
            }
            setFlagLoading((prev) => ({ ...prev, flagRoundLoader: false }));
        },
        [
            getCannedResponseDataApi,
            setCurrentCannedRes,
            handleSendMessageManually,
            userDetail,
            setFlagPreviewAndSendCannedResPopup,
            setFlagLoading,
            handleError,
        ]
    );

    const closeFloatingToolbar = useCallback(() => {
        lastToolbarSelectionKeyRef.current = null;
        setSelection(null);
        setToolbarPosition(null);
    }, []);

    // Restore this thread's draft (or clear) whenever the active thread changes
    useEffect(() => {
        const draft = draftThreadId
            ? (useRuleBasedInboxStore.getState().threadDraftById[draftThreadId] ?? '')
            : '';
        setinputValue(draft);
        setSlashMenu(INITIAL_SLASH_MENU);
        slashStartIndexRef.current = -1;
        lastToolbarSelectionKeyRef.current = null;
        setSelection(null);
        setToolbarPosition(null);
    }, [draftThreadId]);

    const handleSelect = useCallback(() => {
        const el = inputRef.current;
        if (!el) return;

        const start = el.selectionStart ?? 0;
        const end = el.selectionEnd ?? 0;

        if (start === end) {
            closeFloatingToolbar();
            return;
        }

        deactivateSlashMenu();

        const selectedText = el.value.slice(start, end);
        const selectionKey = `${start}:${end}:${selectedText}`;
        if (lastToolbarSelectionKeyRef.current === selectionKey) {
            return;
        }
        lastToolbarSelectionKeyRef.current = selectionKey;

        setSelection({ start, end, text: selectedText });
        setToolbarPosition({
            top: Math.max(8, el.getBoundingClientRect().top - 48),
            left: el.getBoundingClientRect().left,
        });
    }, [closeFloatingToolbar, deactivateSlashMenu]);

    const handleChatInputFormatting = useCallback(
        (format: keyof formatType) => {
            const el = inputRef.current;
            if (!el) return;
            const start = el.selectionStart ?? 0;
            const end = el.selectionEnd ?? 0;
            const currentValue = el.value;

            const selectedText = currentValue?.substring(start, end);
            if (!selectedText) return;
            let wrapped = selectedText;

            if (format === 'bold') wrapped = `*${selectedText}*`;
            if (format === 'italic') wrapped = `_${selectedText}_`;
            if (format === 'strikethrough') wrapped = `~${selectedText}~`;
            if (format === 'bullet') wrapped = `• ${selectedText}`;

            const newValue = currentValue?.substring(0, start) + wrapped + currentValue?.substring(end);
            updateInputValue(newValue);

            scheduleAfterRender(() => {
                el.focus();
                el.setSelectionRange(start + wrapped.length, start + wrapped.length);
                closeFloatingToolbar();
            });
        },
        [closeFloatingToolbar, updateInputValue]
    );

    const handleSelectCannedResponse = useCallback(
        (selectedItem: CannedResponseTemplate) => {
            const startIdx = slashStartIndexRef.current;
            const queryLen = slashQuery.length;
            if (startIdx !== -1) {
                updateInputValue((prev) => prev.substring(0, startIdx) + prev.substring(startIdx + 1 + queryLen));
            }
            setSlashMenu(INITIAL_SLASH_MENU);
            slashStartIndexRef.current = -1;

            const flagDirectSend = userDetail?.flag_send_direct_canned_message;
            getCannedResponseApi(selectedItem?.canned_response_template_id ?? '', flagDirectSend);
        },
        [slashQuery.length, userDetail?.flag_send_direct_canned_message, getCannedResponseApi, updateInputValue]
    );

    const handleSlashKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (!slashCommandActive) return;

            if (filteredCannedResponses.length > 0) {
                if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setSlashMenu((prev) => ({
                        ...prev,
                        highlightedIndex:
                            prev.highlightedIndex <= 0
                                ? filteredCannedResponses.length - 1
                                : prev.highlightedIndex - 1,
                    }));
                } else if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setSlashMenu((prev) => ({
                        ...prev,
                        highlightedIndex:
                            prev.highlightedIndex >= filteredCannedResponses.length - 1
                                ? 0
                                : prev.highlightedIndex + 1,
                    }));
                }
            }

            if (e.key === 'Escape') {
                e.preventDefault();
                setSlashMenu(INITIAL_SLASH_MENU);
                slashStartIndexRef.current = -1;
            } else if (e.key === 'Enter' && !e.shiftKey && filteredCannedResponses.length > 0) {
                e.preventDefault();
                handleSelectCannedResponse(filteredCannedResponses[highlightedIndex]);
            }
        },
        [slashCommandActive, filteredCannedResponses, highlightedIndex, handleSelectCannedResponse]
    );

    const handleKeyPress = useCallback(
        (e: React.KeyboardEvent<HTMLDivElement>) => {
            const el = inputRef.current;
            if (!el && !slashCommandActive) return;

            if (el) {
                const start = el.selectionStart ?? 0;
                const end = el.selectionEnd ?? 0;
                const currentValue = el.value;

                const handleAddBullet = (type: 'tab' | 'space') => {
                    e.preventDefault();
                    let newValue = currentValue;

                    if (type === 'tab') {
                        newValue = currentValue?.slice(0, start) + '• ' + currentValue?.slice(end);
                    } else if (type === 'space') {
                        newValue = currentValue?.slice(0, currentValue?.length - 1) + '• ';
                    }

                    updateInputValue(newValue);

                    scheduleAfterRender(() => {
                        el.focus();
                        el.setSelectionRange(start + 2, start + 2);
                    });
                };

                handleKeyDownForFormatting(e, handleChatInputFormatting);

                if (e.key === 'Tab') {
                    handleAddBullet('tab');
                    return;
                }

                if (e.key === ' ' && currentValue?.endsWith('-')) {
                    handleAddBullet('space');
                    return;
                }

                if (e.key === 'Enter' && !slashCommandActive) {
                    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
                    if (isMobile) return;

                    if (!e.shiftKey) {
                        e.preventDefault();
                        if (currentActiveChatbotThread) {
                            handleSendMessageManually(currentValue, false, false, false, [], null);
                            clearInputValue();
                            deactivateSlashMenu();
                            closeFloatingToolbar();
                        }
                    }
                }
            }
            if (slashCommandActive) {
                handleSlashKeyDown(e);
            }
        },
        [
            slashCommandActive,
            handleChatInputFormatting,
            currentActiveChatbotThread,
            handleSendMessageManually,
            closeFloatingToolbar,
            handleSlashKeyDown,
            deactivateSlashMenu,
            updateInputValue,
            clearInputValue,
        ]
    );

    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            const targetNode = event.target as Node;
            const isInsideInput = inputRef.current?.contains(targetNode);
            const isInsideToolbar = toolbarRef.current?.contains(targetNode);
            if (!isInsideInput && !isInsideToolbar) {
                closeFloatingToolbar();
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, [closeFloatingToolbar]);

    useImperativeHandle(
        ref,
        () => ({
            insertAtCursor: (reaction_emoji_character: string) => {
                if (!reaction_emoji_character || !inputRef.current) return;

                const start = inputRef.current.selectionStart ?? inputRef.current.value.length;
                const end = inputRef.current.selectionEnd ?? inputRef.current.value.length;

                updateInputValue((prev) => prev.slice(0, start) + reaction_emoji_character + prev.slice(end));

                scheduleAfterRender(() => {
                    if (inputRef.current) {
                        const newPos = start + reaction_emoji_character.length;
                        inputRef.current.setSelectionRange(newPos, newPos);
                        inputRef.current.focus();
                    }
                });
            },
        }),
        [updateInputValue]
    );

    const handleSendClick = useCallback(() => {
        handleSendMessageManually(inputValue, false, false, false, [], null);
        clearInputValue();
        deactivateSlashMenu();
    }, [handleSendMessageManually, inputValue, deactivateSlashMenu, clearInputValue]);

    const shouldShowSlashCommandMenu = slashCommandActive && filteredCannedResponses.length > 0;
    const isSendDisabled = isManualOverrideTextfieldDisabled || inputValue?.trim() == '';

    const sendBtnSx = useMemo(
        () => ({
            border: `1px solid ${hexToRgba(dynamicColors.send_msg_icon_color)}`,
            opacity: inputValue?.trim() == '' ? '0.5' : '',
            boxShadow: `0 1px 1px ${hexToRgba(dynamicColors.send_msg_icon_color)}`,
        }),
        [dynamicColors.send_msg_icon_color, inputValue]
    );

    const floatingToolbarSx = useMemo(
        () => (toolbarPosition ? { top: toolbarPosition.top, left: toolbarPosition.left } : undefined),
        [toolbarPosition]
    );

    const textFieldInputProps = useMemo(
        () => ({
            onSelect: handleCursorChange,
            dir: 'auto' as const,
        }),
        [handleCursorChange]
    );

    const textFieldMuiInputProps = useMemo(
        () => ({
            startAdornment,
            endAdornment,
        }),
        [startAdornment, endAdornment]
    );

    return (
        <>
            <Box ref={inputAreaRef} sx={{ width: '100%' }}>
                {shouldShowSlashCommandMenu && (
                    <Box className={styles.slashCommandMenu}>
                        <Box className={styles.slashCommandHeader}>
                            <Typography className={styles.slashCommandTitle}>Canned Responses</Typography>
                        </Box>
                        <Box className={styles.slashCommandList} ref={slashMenuListRef}>
                            {filteredCannedResponses.map((item, index) => (
                                <Box
                                    key={item.canned_response_template_id}
                                    data-index={index}
                                    className={`${styles.slashCommandItem} ${
                                        index === highlightedIndex ? styles.slashCommandItemHighlighted : ''
                                    }`}
                                    onMouseEnter={() =>
                                        setSlashMenu((prev) =>
                                            prev.highlightedIndex === index
                                                ? prev
                                                : { ...prev, highlightedIndex: index }
                                        )
                                    }
                                    onClick={() => handleSelectCannedResponse(item)}
                                >
                                    <Typography className={styles.slashCommandItemTitle}>
                                        {`${item.template_title} (${item?.canned_response_messages?.length})`}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}

                <Grid container gap={1} flexWrap="nowrap" alignItems="flex-end">
                    <Grid item className={styles.chatInputInnerGrid} sx={textfieldWrapperStyle}>
                        <TextField
                            multiline
                            value={inputValue}
                            onKeyDown={handleKeyPress}
                            disabled={isTextFieldDisabled}
                            placeholder={placeholder}
                            inputRef={inputRef}
                            onChange={handleInputChange}
                            onSelect={handleSelect}
                            onMouseUp={handleSelect}
                            onBlur={closeFloatingToolbar}
                            inputProps={textFieldInputProps}
                            InputProps={textFieldMuiInputProps}
                            className={styles.chatInputTextField}
                            sx={textFieldSx}
                        />
                    </Grid>

                    {shouldShowSendBtn && (
                        <Grid item className={styles.chatbot_preview_arrow_item} sx={sendBtnGridSx}>
                            <IconButton
                                onClick={handleSendClick}
                                disabled={isSendDisabled}
                                className={styles.sendMessageIconBtn}
                                sx={sendBtnSx}
                            >
                                <IconsColor
                                    fillColor={dynamicColors.send_msg_icon_color}
                                    iconName="send"
                                    width="18px"
                                    height="18px"
                                />
                            </IconButton>
                        </Grid>
                    )}
                </Grid>
            </Box>

            {selection && toolbarPosition && (
                <Box ref={toolbarRef} className={styles.floatingToolbar} sx={floatingToolbarSx}>
                    {TOOLBAR_ITEMS.map((item: toolbarItemsType) => (
                        <IconButton
                            key={item.key}
                            className={styles.floatingToolbarBtn}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleChatInputFormatting(item?.key as keyof formatType)}
                            title={item?.label}
                            size={item?.fontSize ?? 'small'}
                        >
                            {item?.icon}
                        </IconButton>
                    ))}
                </Box>
            )}
        </>
    );
});

export default memo(ChatTextField);
